import React, { useMemo, useState } from 'react';
import useGameStore from '../../store/gameStore';
import { Download, FileSpreadsheet, Filter, History, BookOpen, FileDown } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import {
  downloadContractHistoryCsv,
  downloadContractCatalogueCsv,
} from '../../utils/contractHistoryExport';
import { CONTRACTS } from '../../data/contracts';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

/**
 * ContractLedgerPanel — pedagogical analytics panel surfacing every
 * contract that ever appeared in the player's career, with status
 * counters and a CSV export trigger for offline balancing.
 *
 * Created 08/02/2026 (soir 9) in response to a user feedback document
 * asking for an "historique des contrats avec extraction pour analyse"
 * so the game balance can be tuned with real data, not gut feeling.
 *
 * Lives on /strategie because it's a strategic / meta-game tool, not a
 * gameplay action. Read-only.
 */
const STATUS_META = {
  proposed:  { label: 'Proposés',  cls: 'text-slate-300 bg-slate-500/15 border-slate-500/30' },
  accepted:  { label: 'Acceptés',  cls: 'text-money bg-money/10 border-money/30' },
  completed: { label: 'Terminés',  cls: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
  expired:   { label: 'Expirés',   cls: 'text-warn bg-warn/10 border-warn/30' },
  cancelled: { label: 'Annulés',   cls: 'text-danger bg-danger/10 border-danger/30' },
  displaced: { label: 'Déplacés',  cls: 'text-purple-300 bg-purple-500/15 border-purple-500/30' },
};

const STATUS_ORDER = ['proposed', 'accepted', 'completed', 'expired', 'cancelled', 'displaced'];

export default function ContractLedgerPanel() {
  const ledger = useGameStore(s => s.contractLedger || EMPTY_ARRAY);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showRows, setShowRows] = useState(false);

  const stats = useMemo(() => {
    const acc = { total: ledger.length, acceptedValue: 0, completedValue: 0 };
    STATUS_ORDER.forEach(s => { acc[s] = 0; });
    for (const e of ledger) {
      acc[e.status] = (acc[e.status] || 0) + 1;
      if (['accepted', 'completed', 'cancelled', 'displaced'].includes(e.status)) {
        acc.acceptedValue += e.frequency === 'ponctuel'
          ? (e.acceptedRevenue || e.revenue || 0)
          : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.duration || 0));
      }
      if (e.status === 'completed') {
        acc.completedValue += e.frequency === 'ponctuel'
          ? (e.acceptedRevenue || e.revenue || 0)
          : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.daysServed || e.duration || 0));
      }
    }
    acc.acceptanceRate = acc.total > 0
      ? Math.round(((acc.accepted + acc.completed + acc.cancelled + acc.displaced) / acc.total) * 100)
      : 0;
    return acc;
  }, [ledger]);

  const categories = useMemo(() => {
    const set = new Set();
    ledger.forEach(e => { if (e.category) set.add(e.category); });
    return Array.from(set).sort();
  }, [ledger]);

  const filteredRows = useMemo(() => {
    return ledger.filter(e =>
      (statusFilter === 'all' || e.status === statusFilter)
      && (categoryFilter === 'all' || e.category === categoryFilter),
    );
  }, [ledger, statusFilter, categoryFilter]);

  const handleExportHistory = () => downloadContractHistoryCsv(ledger);
  const handleExportCatalogue = () => downloadContractCatalogueCsv(CONTRACTS);
  const handleDownloadWorkbook = async () => {
    // Fetched via the gated backend route — works only when the backend env
    // var ENABLE_BALANCING_TOOLS=true (set on preview, off in prod).
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/balancing-workbook`;
    try {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        // eslint-disable-next-line no-alert
        window.alert("Classeur indisponible sur cet environnement.");
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'SCD-contracts-balancing.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (_err) {
      // eslint-disable-next-line no-alert
      window.alert("Téléchargement impossible (réseau).");
    }
  };

  // Author tools are hidden in production deploys (NODE_ENV is set by CRA's
  // build step). Belt-and-braces with the backend env gate.
  const showAuthorTools = process.env.NODE_ENV !== 'production';

  return (
    <div className="bg-ink-800/60 border border-ink-600 rounded-2xl p-5 mt-6" data-testid="contract-ledger-panel">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-accent/15">
            <History className="w-4 h-4 text-accent" />
          </div>
          <div>
            <h3 className="font-bold text-slate-100">Historique des contrats</h3>
            <p className="text-[11px] text-slate-500">
              Journal append-only des contrats proposés depuis le début de la partie. Exportable pour analyse offline.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showAuthorTools && (
            <>
              <button
                type="button"
                onClick={handleDownloadWorkbook}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors flex items-center gap-1.5"
                data-testid="download-balancing-xlsx-btn"
                title="Outil auteur — masqué en production. Télécharger le classeur Excel de calibrage (catalogue + calendrier 24 mois)."
              >
                <FileDown className="w-3 h-3" /> Classeur calibrage (.xlsx)
              </button>
              <button
                type="button"
                onClick={handleExportCatalogue}
                className="text-xs px-3 py-1.5 rounded-md bg-ink-700 border border-ink-600 text-slate-300 hover:bg-ink-600 transition-colors flex items-center gap-1.5"
                data-testid="export-catalogue-csv-btn"
                title="Outil auteur — masqué en production. Exporter le catalogue complet des modèles de contrats (template pour calibrage)."
              >
                <BookOpen className="w-3 h-3" /> Catalogue (.csv)
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleExportHistory}
            disabled={ledger.length === 0}
            className="text-xs px-3 py-1.5 rounded-md bg-accent/20 border border-accent/40 text-accent hover:bg-accent/30 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="export-history-csv-btn"
            title="Exporter votre journal personnel de contrats en CSV (proposés/acceptés/terminés)"
          >
            <Download className="w-3 h-3" /> Mon journal (.csv)
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <StatCard label="Total proposés" value={stats.total} testId="ledger-stat-total" />
        <StatCard label="Taux d'acceptation" value={`${stats.acceptanceRate}%`} testId="ledger-stat-acceptance" />
        <StatCard label="Valeur engagée" value={formatMoney(stats.acceptedValue)} testId="ledger-stat-accepted-value" />
        <StatCard label="Valeur réalisée" value={formatMoney(stats.completedValue)} testId="ledger-stat-completed-value" />
      </div>

      {/* Status breakdown */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="ledger-status-breakdown">
        {STATUS_ORDER.map(s => (
          <span
            key={s}
            className={`text-[11px] px-2 py-1 rounded-md border font-mono ${STATUS_META[s].cls}`}
            data-testid={`ledger-status-${s}`}
          >
            {STATUS_META[s].label} <strong>{stats[s] || 0}</strong>
          </span>
        ))}
      </div>

      {/* Filters + table toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Filter className="w-3 h-3 text-slate-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-ink-700 border border-ink-600 text-slate-200 rounded px-2 py-1 text-xs"
            data-testid="ledger-filter-status"
          >
            <option value="all">Tous statuts</option>
            {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-ink-700 border border-ink-600 text-slate-200 rounded px-2 py-1 text-xs"
            data-testid="ledger-filter-category"
          >
            <option value="all">Toutes catégories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setShowRows(v => !v)}
          className="text-xs text-slate-400 hover:text-slate-200 underline"
          data-testid="ledger-toggle-rows"
        >
          {showRows ? 'Masquer le détail' : 'Voir le détail'} ({filteredRows.length})
        </button>
      </div>

      {/* Detail table — virtualised via CSS scrollbar, capped to 200 rows */}
      {showRows && (
        <div className="border border-ink-600 rounded-lg overflow-hidden" data-testid="ledger-rows-table">
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-ink-700/60 text-slate-400 uppercase tracking-wider text-[9px]">
                <tr>
                  <th className="text-left px-2 py-1.5">Jour</th>
                  <th className="text-left px-2 py-1.5">Contrat</th>
                  <th className="text-left px-2 py-1.5">Catégorie</th>
                  <th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-right px-2 py-1.5">Valeur</th>
                  <th className="text-left px-2 py-1.5">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700">
                {filteredRows.slice(-200).reverse().map(e => {
                  const value = e.frequency === 'ponctuel'
                    ? (e.acceptedRevenue || e.revenue || 0)
                    : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.duration || 0));
                  const meta = STATUS_META[e.status] || STATUS_META.proposed;
                  return (
                    <tr key={e.instanceId} className="hover:bg-ink-700/30">
                      <td className="px-2 py-1 font-mono text-slate-500">J{e.spawnDay}</td>
                      <td className="px-2 py-1 text-slate-200 truncate max-w-[200px]" title={e.name}>{e.name}</td>
                      <td className="px-2 py-1 text-slate-400">{e.category || '—'}</td>
                      <td className="px-2 py-1 text-slate-400">{e.frequency}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-200">{formatMoney(value)}</td>
                      <td className="px-2 py-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredRows.length > 200 && (
            <p className="text-[10px] text-slate-500 italic px-2 py-1 border-t border-ink-700 bg-ink-700/30">
              {filteredRows.length - 200} ligne(s) plus anciennes masquées — utilisez l&apos;export CSV pour la vue complète.
            </p>
          )}
        </div>
      )}

      <p className="text-[10px] text-slate-500 italic mt-3 flex items-center gap-1.5">
        <FileSpreadsheet className="w-3 h-3" />
        Le CSV est encodé UTF-8 avec BOM, séparateur « ; » — ouvrable directement dans Excel / LibreOffice. La colonne <code className="text-accent">unlockOnDay</code> du catalogue est laissée vide pour vous permettre de planifier vos dates de déblocage.
      </p>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="bg-ink-700/30 border border-ink-600 rounded-lg p-2.5 text-center" data-testid={testId}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">{label}</p>
      <p className="text-base font-black font-mono text-slate-200">{value}</p>
    </div>
  );
}
